const TOKEN_KEY = 'token';
const USER_KEY = 'user';

type StorageMode = 'local' | 'session';

const authChangedEvent = () => new Event('auth-changed');
const userUpdatedEvent = () => new Event('user-updated');

const getStorageByMode = (mode: StorageMode) =>
  mode === 'local' ? localStorage : sessionStorage;

const clearAuthStorage = (storage: Storage) => {
  storage.removeItem(TOKEN_KEY);
  storage.removeItem(USER_KEY);
};

const clearAllAuthStorage = () => {
  clearAuthStorage(localStorage);
  clearAuthStorage(sessionStorage);
};

const decodeJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const [, payload = ''] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string) => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 <= Date.now() + 5000;
};

const getStorageWithToken = (): Storage | null => {
  if (localStorage.getItem(TOKEN_KEY)) return localStorage;
  if (sessionStorage.getItem(TOKEN_KEY)) return sessionStorage;
  return null;
};

const getValidTokenFromStorage = (storage: Storage | null): string | null => {
  const token = storage?.getItem(TOKEN_KEY);
  if (!token) return null;

  if (isTokenExpired(token)) {
    clearAllAuthStorage();
    return null;
  }

  return token;
};

export const setToken = (token: string, rememberMe = false) => {
  clearAllAuthStorage();
  getStorageByMode(rememberMe ? 'local' : 'session').setItem(TOKEN_KEY, token);
  window.dispatchEvent(authChangedEvent());
};

export const setStoredUser = (user: any, rememberMe?: boolean) => {
  const activeStorage =
    getStorageWithToken() ||
    getStorageByMode(rememberMe ? 'local' : 'session');

  activeStorage.setItem(USER_KEY, JSON.stringify(user || {}));
  window.dispatchEvent(userUpdatedEvent());
};

export const setAuthSession = ({
  token,
  user,
  rememberMe = false,
}: {
  token: string;
  user?: any;
  rememberMe?: boolean;
}) => {
  setToken(token, rememberMe);
  if (user) {
    setStoredUser(user, rememberMe);
  }
};

export const getToken = (): string | null => {
  return getValidTokenFromStorage(getStorageWithToken());
};

export const getStoredUser = <T = any>(): T | null => {
  const storage = getStorageWithToken();
  const token = getValidTokenFromStorage(storage);
  if (!token || !storage) return null;

  try {
    const raw = storage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

export const removeToken = () => {
  clearAllAuthStorage();
  window.dispatchEvent(authChangedEvent());
};

export const removeStoredUser = () => {
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(USER_KEY);
  window.dispatchEvent(userUpdatedEvent());
};

export const clearAuthSession = () => {
  clearAllAuthStorage();
  window.dispatchEvent(authChangedEvent());
  window.dispatchEvent(userUpdatedEvent());
};

export const isAuthenticated = (): boolean => {
  return !!getToken();
};
